/**
 * Countries, for the flag in front of a telephone number.
 *
 * Two columns only — the ISO 3166-1 alpha-2 code and the dialling code. The
 * name is asked of `Intl.DisplayNames`, so the list reads in whatever language
 * the app is set to instead of carrying 250 English strings that Filipino has
 * no translation for. The flag is derived from the code as well: a regional
 * indicator pair *is* the flag emoji, so there is no image to ship and nothing
 * to fetch.
 *
 * Not a validation table. Nothing here dials a number or checks its length —
 * this picks which flag sits in the box and what `+` prefix a blank field
 * starts from.
 */

/** `[ISO 3166-1 alpha-2, dialling code]`, lower-cased and without the plus. */
const DIAL: ReadonlyArray<readonly [string, string]> = [
  ["af", "93"],
  ["al", "355"],
  ["dz", "213"],
  ["as", "1684"],
  ["ad", "376"],
  ["ao", "244"],
  ["ai", "1264"],
  ["ag", "1268"],
  ["ar", "54"],
  ["am", "374"],
  ["aw", "297"],
  ["au", "61"],
  ["at", "43"],
  ["az", "994"],
  ["bs", "1242"],
  ["bh", "973"],
  ["bd", "880"],
  ["bb", "1246"],
  ["by", "375"],
  ["be", "32"],
  ["bz", "501"],
  ["bj", "229"],
  ["bm", "1441"],
  ["bt", "975"],
  ["bo", "591"],
  ["ba", "387"],
  ["bw", "267"],
  ["br", "55"],
  ["io", "246"],
  ["vg", "1284"],
  ["bn", "673"],
  ["bg", "359"],
  ["bf", "226"],
  ["bi", "257"],
  ["kh", "855"],
  ["cm", "237"],
  ["ca", "1"],
  ["cv", "238"],
  ["bq", "599"],
  ["ky", "1345"],
  ["cf", "236"],
  ["td", "235"],
  ["cl", "56"],
  ["cn", "86"],
  ["co", "57"],
  ["km", "269"],
  ["cd", "243"],
  ["cg", "242"],
  ["ck", "682"],
  ["cr", "506"],
  ["ci", "225"],
  ["hr", "385"],
  ["cu", "53"],
  ["cw", "599"],
  ["cy", "357"],
  ["cz", "420"],
  ["dk", "45"],
  ["dj", "253"],
  ["dm", "1767"],
  ["do", "1809"],
  ["ec", "593"],
  ["eg", "20"],
  ["sv", "503"],
  ["gq", "240"],
  ["er", "291"],
  ["ee", "372"],
  ["sz", "268"],
  ["et", "251"],
  ["fk", "500"],
  ["fo", "298"],
  ["fj", "679"],
  ["fi", "358"],
  ["fr", "33"],
  ["gf", "594"],
  ["pf", "689"],
  ["ga", "241"],
  ["gm", "220"],
  ["ge", "995"],
  ["de", "49"],
  ["gh", "233"],
  ["gi", "350"],
  ["gr", "30"],
  ["gl", "299"],
  ["gd", "1473"],
  ["gp", "590"],
  ["gu", "1671"],
  ["gt", "502"],
  ["gg", "44"],
  ["gn", "224"],
  ["gw", "245"],
  ["gy", "592"],
  ["ht", "509"],
  ["hn", "504"],
  ["hk", "852"],
  ["hu", "36"],
  ["is", "354"],
  ["in", "91"],
  ["id", "62"],
  ["ir", "98"],
  ["iq", "964"],
  ["ie", "353"],
  ["im", "44"],
  ["il", "972"],
  ["it", "39"],
  ["jm", "1876"],
  ["jp", "81"],
  ["je", "44"],
  ["jo", "962"],
  ["kz", "7"],
  ["ke", "254"],
  ["ki", "686"],
  ["xk", "383"],
  ["kw", "965"],
  ["kg", "996"],
  ["la", "856"],
  ["lv", "371"],
  ["lb", "961"],
  ["ls", "266"],
  ["lr", "231"],
  ["ly", "218"],
  ["li", "423"],
  ["lt", "370"],
  ["lu", "352"],
  ["mo", "853"],
  ["mg", "261"],
  ["mw", "265"],
  ["my", "60"],
  ["mv", "960"],
  ["ml", "223"],
  ["mt", "356"],
  ["mh", "692"],
  ["mq", "596"],
  ["mr", "222"],
  ["mu", "230"],
  ["yt", "262"],
  ["mx", "52"],
  ["fm", "691"],
  ["md", "373"],
  ["mc", "377"],
  ["mn", "976"],
  ["me", "382"],
  ["ms", "1664"],
  ["ma", "212"],
  ["mz", "258"],
  ["mm", "95"],
  ["na", "264"],
  ["nr", "674"],
  ["np", "977"],
  ["nl", "31"],
  ["nc", "687"],
  ["nz", "64"],
  ["ni", "505"],
  ["ne", "227"],
  ["ng", "234"],
  ["nu", "683"],
  ["nf", "672"],
  ["kp", "850"],
  ["mk", "389"],
  ["mp", "1670"],
  ["no", "47"],
  ["om", "968"],
  ["pk", "92"],
  ["pw", "680"],
  ["ps", "970"],
  ["pa", "507"],
  ["pg", "675"],
  ["py", "595"],
  ["pe", "51"],
  ["ph", "63"],
  ["pl", "48"],
  ["pt", "351"],
  ["pr", "1787"],
  ["qa", "974"],
  ["re", "262"],
  ["ro", "40"],
  ["ru", "7"],
  ["rw", "250"],
  ["bl", "590"],
  ["sh", "290"],
  ["kn", "1869"],
  ["lc", "1758"],
  ["mf", "590"],
  ["pm", "508"],
  ["vc", "1784"],
  ["ws", "685"],
  ["sm", "378"],
  ["st", "239"],
  ["sa", "966"],
  ["sn", "221"],
  ["rs", "381"],
  ["sc", "248"],
  ["sl", "232"],
  ["sg", "65"],
  ["sx", "1721"],
  ["sk", "421"],
  ["si", "386"],
  ["sb", "677"],
  ["so", "252"],
  ["za", "27"],
  ["kr", "82"],
  ["ss", "211"],
  ["es", "34"],
  ["lk", "94"],
  ["sd", "249"],
  ["sr", "597"],
  ["se", "46"],
  ["ch", "41"],
  ["sy", "963"],
  ["tw", "886"],
  ["tj", "992"],
  ["tz", "255"],
  ["th", "66"],
  ["tl", "670"],
  ["tg", "228"],
  ["tk", "690"],
  ["to", "676"],
  ["tt", "1868"],
  ["tn", "216"],
  ["tr", "90"],
  ["tm", "993"],
  ["tc", "1649"],
  ["tv", "688"],
  ["ug", "256"],
  ["ua", "380"],
  ["ae", "971"],
  ["gb", "44"],
  ["us", "1"],
  ["uy", "598"],
  ["uz", "998"],
  ["vu", "678"],
  ["va", "39"],
  ["ve", "58"],
  ["vn", "84"],
  ["vi", "1340"],
  ["wf", "681"],
  ["ye", "967"],
  ["zm", "260"],
  ["zw", "263"],
] as const;

export interface Country {
  /** ISO 3166-1 alpha-2, lower-cased. What the API stores. */
  code: string;
  /** Dialling code without the plus: "63". */
  dial: string;
  /** The flag, as two regional indicator letters. */
  flag: string;
}

/**
 * The flag emoji for a country code.
 *
 * A flag is its two letters written as regional indicator symbols — U+1F1E6 is
 * "A" — which every platform that draws flags at all draws from the pair. The
 * few that do not (Windows) show the two letters instead, which is still the
 * country, so there is nothing to fall back to.
 */
export function flagOf(code: string): string {
  return code
    .toUpperCase()
    .replace(/[^A-Z]/g, "")
    .replace(/./g, (letter) => String.fromCodePoint(127_397 + letter.charCodeAt(0)));
}

export const COUNTRIES: readonly Country[] = DIAL.map(([code, dial]) => ({
  code,
  dial,
  flag: flagOf(code),
}));

const BY_CODE = new Map(COUNTRIES.map((country) => [country.code, country]));

export const findCountry = (code: string | null | undefined): Country | undefined =>
  code ? BY_CODE.get(code.toLowerCase()) : undefined;

/**
 * Where every phone field starts when nothing has been chosen.
 *
 * The Philippines: this is a Philippine club's staff system, and the number a
 * person here is actually reachable on is a local one. A picker that opens on
 * the wrong country is a picker everybody has to change before they can type.
 */
export const DEFAULT_COUNTRY = "ph";

/**
 * The country's name in the app's language, or its code if the browser cannot
 * say. `Intl.DisplayNames` is everywhere current, but it throws on a locale it
 * does not know rather than falling back, so both the lookup and the
 * construction are guarded — a picker of 250 blanks would be worse than a
 * picker of 250 codes.
 */
const displayNames = (locale: string): Intl.DisplayNames | null => {
  try {
    return new Intl.DisplayNames([locale, "en"], { type: "region" });
  } catch {
    return null;
  }
};

const NAMES = new Map<string, Intl.DisplayNames | null>();

export function countryName(code: string, locale = "en"): string {
  if (!NAMES.has(locale)) NAMES.set(locale, displayNames(locale));
  try {
    return NAMES.get(locale)?.of(code.toUpperCase()) ?? code.toUpperCase();
  } catch {
    return code.toUpperCase();
  }
}

/**
 * The list as the picker shows it: named, and in the language's own order.
 *
 * Sorted with a collator rather than by code points, so accented names land
 * where a reader of that language looks for them — Åland under A in English,
 * last in Swedish.
 */
export function namedCountries(locale = "en"): Array<Country & { name: string }> {
  const collator = new Intl.Collator(locale);
  return COUNTRIES.map((country) => ({
    ...country,
    name: countryName(country.code, locale),
  })).sort((a, b) => collator.compare(a.name, b.name));
}

/**
 * The digits of a number, without its dialling code — what goes in the box
 * once the flag in front of it is saying the rest.
 *
 * Everything that is not a digit goes first. What this app writes is plain, but
 * the column has always accepted spacing and punctuation, and a row written
 * before it did — or by anything other than this form — must not come back as
 * a number with a space wedged into the middle of it.
 */
export function localPart(phone: string, country: Country | undefined): string {
  const digits = phone.replace(/\D/g, "");
  if (country && digits.startsWith(country.dial)) return digits.slice(country.dial.length);
  return digits;
}

/**
 * How a country writes its own numbers, one `.` per digit.
 *
 * Only where the convention is strong enough that even grouping would read
 * wrong — the North American `(650) 555-0123` is the case everybody knows, and
 * it is the one the design asks for. Everywhere else is grouped rather than
 * masked: a wrong mask fights what somebody is typing, and spaces in the wrong
 * place are merely spaces in the wrong place.
 */
const MASKS: Record<string, string> = {
  /** Every +1 country: the NANP writes them all the same way. */
  "1": "(...) ...-....",
};

/** A mask filled with digits, stopping wherever the digits run out. */
function applyMask(mask: string, digits: string): string {
  let out = "";
  let at = 0;
  for (const slot of mask) {
    if (at >= digits.length) break;
    if (slot === ".") {
      out += digits[at];
      at += 1;
    } else {
      out += slot;
    }
  }
  // A number longer than its own mask is still that number. Better to show
  // every digit unspaced at the end than to drop the ones that did not fit.
  return at < digits.length ? `${out}${digits.slice(at)}` : out;
}

/**
 * Digits in threes, with the last group taking the remainder.
 *
 * The fallback for the 240-odd countries with no mask here. Ten digits read
 * "917 123 4567" rather than "917 123 456 7", which is the one thing a naive
 * grouping gets visibly wrong.
 */
function group(digits: string): string {
  const groups: string[] = [];
  let at = 0;
  while (digits.length - at > 4) {
    groups.push(digits.slice(at, at + 3));
    at += 3;
  }
  if (at < digits.length) groups.push(digits.slice(at));
  return groups.join(" ");
}

/** The part after the dialling code, spaced the way that country writes it. */
export function formatNational(country: Country | undefined, digits: string): string {
  if (!digits) return "";
  const mask = country ? MASKS[country.dial] : undefined;
  return mask ? applyMask(mask, digits) : group(digits);
}

/**
 * How many digits a country's own numbers run to, after the dialling code.
 *
 * Not a substitute for libphonenumber — that is a megabyte of metadata to
 * answer a question this form does not ask. It catches the mistake people
 * actually make: a number typed short, or a ten-digit number left in the box
 * after the flag beside it was changed to a country whose numbers are eight.
 *
 * Only the countries this club's staff and members actually dial are listed,
 * and each range is deliberately wide enough to admit landlines as well as
 * mobiles. Everything else falls back to `E164_*` below, which is the rule
 * every telephone number in the world obeys.
 */
const LENGTHS: Record<string, readonly [number, number]> = {
  ph: [8, 10],
  us: [10, 10],
  ca: [10, 10],
  gb: [9, 10],
  au: [9, 9],
  nz: [8, 10],
  sg: [8, 8],
  hk: [8, 8],
  mo: [8, 8],
  my: [9, 10],
  id: [9, 12],
  th: [9, 9],
  vn: [9, 10],
  jp: [10, 11],
  kr: [9, 10],
  cn: [11, 11],
  tw: [9, 9],
  in: [10, 10],
  ae: [9, 9],
  sa: [9, 9],
  qa: [8, 8],
  kw: [8, 8],
  bh: [8, 8],
  om: [8, 8],
  il: [9, 9],
  de: [10, 11],
  fr: [9, 9],
  es: [9, 9],
  it: [9, 10],
  nl: [9, 9],
  be: [8, 9],
  ch: [9, 9],
  at: [10, 11],
  se: [7, 9],
  no: [8, 8],
  dk: [8, 8],
  fi: [9, 10],
  ie: [9, 9],
  pt: [9, 9],
  pl: [9, 9],
  ru: [10, 10],
  tr: [10, 10],
  za: [9, 9],
  ng: [10, 10],
  ke: [9, 9],
  eg: [10, 10],
  br: [10, 11],
  mx: [10, 10],
  ar: [10, 11],
  cl: [9, 9],
  co: [10, 10],
  pe: [9, 9],
};

/** E.164 caps the whole number — dialling code included — at fifteen digits. */
const E164_MAX = 15;
/** Shorter than this is not a number anybody typed on purpose. */
const E164_MIN = 4;

/** What a country's own numbers may run to, after the dialling code. */
export function nationalLength(country: Country | undefined): readonly [number, number] {
  const known = country ? LENGTHS[country.code] : undefined;
  if (known) return known;
  // No entry: allow anything E.164 itself would, given what the dialling code
  // has already spent of the fifteen.
  return [E164_MIN, Math.max(E164_MIN, E164_MAX - (country?.dial.length ?? 0))];
}

/** Why a number is not one, or null when it is fine. Empty is fine — see `hasNumber`. */
export type PhoneProblem = { kind: "short" | "long"; min: number; max: number } | null;

/**
 * Whether a number could be a number in the country beside it.
 *
 * Length only, and deliberately: a rule strict enough to know a real Philippine
 * mobile prefix from an unreal one is a rule that refuses somebody's perfectly
 * good landline the week a new prefix is issued. Refusing what cannot possibly
 * be right, and accepting everything else, is the honest amount of certainty a
 * form has about a telephone number.
 */
export function phoneProblem(country: Country | undefined, phone: string): PhoneProblem {
  const digits = localPart(phone, country);
  if (!digits) return null;
  const [min, max] = nationalLength(country);
  if (digits.length < min) return { kind: "short", min, max };
  if (digits.length > max) return { kind: "long", min, max };
  return null;
}

/**
 * A stored number as it is read: `+63 917 123 4567`, `+1 (650) 555-0123`.
 *
 * What goes in the box, and what is shown wherever a number is displayed. The
 * stored value keeps no spacing of its own — see `retypePhone` — so formatting
 * is decided here, once, rather than frozen into the row at the moment it was
 * typed.
 */
export function formatPhone(country: Country | undefined, phone: string): string {
  const dial = country?.dial ?? "";
  const national = formatNational(country, localPart(phone, country));
  if (!dial) return national ? `+${national}` : "";
  return national ? `+${dial} ${national}` : `+${dial}`;
}

/**
 * What was typed, back to the one form the API stores: `+` then digits, no
 * spacing.
 *
 * The dialling code is put back on whatever happened to the text, which is
 * what makes it uneditable — legacy's `countryCodeEditable={false}`. Backing
 * over it would otherwise silently change which country the number is in
 * without moving the flag beside it.
 */
export function retypePhone(typed: string, country: Country | undefined): string {
  const dial = country?.dial ?? "";
  const digits = typed.replace(/\D/g, "");
  const local = digits.startsWith(dial) ? digits.slice(dial.length) : digits;
  return `+${dial}${local.slice(0, 20)}`;
}
